import { useMemo } from "react";
import { parseMarkdown, type Block, type Inline } from "../../lib/markdown";

/**
 * House Rules, rendered as the document it is (PRD FR-2.1): readable,
 * anchored per section, and worth screenshotting. Parsed to a typed tree —
 * no HTML string ever reaches the DOM.
 */
export function HouseRulesViewer({ markdown }: { markdown: string }) {
  const blocks = useMemo(() => parseMarkdown(markdown), [markdown]);

  if (blocks.length === 0) {
    return <p className="text-sm text-faint">Your House Rules are not available to read yet.</p>;
  }

  return (
    <article className="nob-rules">
      {blocks.map((block, i) => (
        <BlockView key={i} block={block} />
      ))}
    </article>
  );
}

function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case "heading": {
      const inner = <InlineRun inlines={block.inlines} />;
      if (block.level === 1) {
        return (
          <h1
            id={block.id}
            className="mb-4 font-mono text-base font-medium tracking-tight text-accent-ink"
          >
            {inner}
          </h1>
        );
      }
      if (block.level === 2) {
        return (
          <h2
            id={block.id}
            className="mt-5 mb-2 border-t border-border/80 pt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-faint first:mt-0 first:border-0 first:pt-0"
          >
            {inner}
          </h2>
        );
      }
      return (
        <h3 id={block.id} className="mt-4 mb-1.5 text-[13px] font-semibold text-text-soft">
          {inner}
        </h3>
      );
    }
    case "list":
      return (
        <ul className="my-3 space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="relative pl-5 text-[13px] leading-snug text-text-soft">
              <span
                aria-hidden
                className="absolute left-0 top-[0.62em] h-1 w-1 rounded-full bg-raised-strong"
              />
              <InlineRun inlines={item} />
            </li>
          ))}
        </ul>
      );
    case "paragraph":
      return (
        <p className="my-2 text-[13px] leading-snug text-text-soft">
          <InlineRun inlines={block.inlines} />
        </p>
      );
  }
}

function InlineRun({ inlines }: { inlines: Inline[] }) {
  return (
    <>
      {inlines.map((node, i) => {
        switch (node.kind) {
          case "strong":
            return (
              <strong key={i} className="font-semibold text-text">
                {node.text}
              </strong>
            );
          case "em":
            return (
              <em key={i} className="italic text-text-soft">
                {node.text}
              </em>
            );
          case "code":
            return (
              <code
                key={i}
                className="rounded bg-surface px-1.5 py-0.5 font-mono text-[13px] text-accent-ink-strong"
              >
                {node.text}
              </code>
            );
          case "text":
            return <span key={i}>{node.text}</span>;
        }
      })}
    </>
  );
}
